// SuperAdmin billing. Every row here comes from the platform's real Invoice
// table via GET /admin/invoices — this page used to fabricate its invoices in
// the browser from the USER list (hardcoded ₹3499/₹999 amounts, Math.random()
// timestamps) and its "Create invoice" button only closed the modal.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Eye, Loader2, WifiOff, Search, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import LoadError from "@/components/dashboard/LoadError";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { listInvoices, createInvoice, listCompanies, getPlans } from "@/api/admin";
import { toastError, toastSuccess } from "@/lib/toast";
import { formatDate } from "@/lib/dates";
import { BASE_CURRENCY, formatMoney } from "@/lib/money";
import { invoiceNumber } from "@/lib/format";

const STATUS_STYLE = {
  paid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300",
  unpaid: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
  failed: "bg-rose-500/15 text-rose-600 dark:text-rose-300",
};
// A generic fallback rather than a specific (removed, #67) status key, so an
// unrecognized status still renders a badge instead of an unstyled one.
const STATUS_STYLE_DEFAULT = "bg-slate-200/80 text-slate-500 dark:bg-white/10 dark:text-slate-400";

// Mirrors INVOICE_STATUSES on the backend. refunded / partial_refund / voided
// were dropped (#67) — nothing in this app ever sets an invoice to any of
// those three (there is no refund flow at all), so they were filter options
// that could never match a single row.
const STATUS_FILTERS = ["all", "paid", "unpaid", "failed"];
const PAGE_SIZE = 25;
// `sort` is the server-side column name; a column without one isn't sortable.
const COLUMNS = [
  { label: "INVOICE_ID" },
  { label: "COMPANY", sort: "company_name" },
  { label: "AMOUNT", sort: "amount" },
  { label: "PLAN", sort: "plan_id" },
  { label: "STATUS", sort: "status" },
  { label: "PERIOD", sort: "period_end" },
  { label: "CREATED", sort: "created_at" },
  { label: "ACTIONS" },
];

// Formats in the invoice's own currency. The old version printed a rupee sign
// for INR and NOTHING AT ALL for anything else — a $49 invoice rendered as a
// bare "49.00" with no way to tell what it was — and forced two decimals onto
// currencies that have none.
const money = (amount, currency) =>
  amount == null ? "—" : formatMoney(Number(amount), currency || BASE_CURRENCY);

const fmtDate = (v) => formatDate(v);

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  // Per-currency totals: {INR: 120000, USD: 490}. A single "gross" number stopped
  // being meaningful the moment an invoice could be raised in another currency —
  // adding dollars to rupees produces a figure that is not money.
  const [grossByCurrency, setGrossByCurrency] = useState({});
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newInvoice, setNewInvoice] = useState({
    company_id: "", plan_id: "manual", amount: "", status: "unpaid",
    period_start: "", period_end: "", payment_reference: "", notes: "",
  });
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  // The server's TRUE match count, over the whole filtered set. It has always
  // been returned (`total`) precisely so this page could not undercount, and was
  // ignored — so the header said "200 invoices" on a platform with thousands and
  // the pager could not reach past the newest page the server happened to send.
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");   // debounced; what is sent
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("desc");

  // Click a header: same column flips direction, a new column starts descending
  // (newest / largest first is what an operator wants on every one of these).
  function toggleSort(col) {
    if (sort === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(col); setDir("desc"); }
    setPage(1);
  }

  // Clicking through the filter chips fires one request per click. Only the
  // newest may write state, or a slow earlier response repaints the table (and
  // the "collected" total) under whichever chip is currently highlighted.
  const reqId = useRef(0);

  // Filters are applied server-side, so the row count and the table can never
  // disagree — the date pickers used to be pure decoration.
  // Typing must not fire a request per keystroke now that search is server-side.
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Any change to the criteria means page 1 — staying on page 7 of a narrower
  // result set shows an empty table and reads as "no invoices".
  useEffect(() => { setPage(1); }, [statusFilter, fromDate, toDate, searchTerm]);

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    setLoading(true);
    setError(null);
    try {
      const res = await listInvoices({
        status: statusFilter === "all" ? undefined : statusFilter,
        date_from: fromDate || undefined,
        date_to: toDate || undefined,
        search: searchTerm || undefined,
        sort,
        dir,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      if (mine !== reqId.current) return;
      setInvoices(Array.isArray(res.invoices) ? res.invoices : []);
      setTotal(Number.isFinite(res.total) ? res.total : (res.invoices || []).length);
      setGrossByCurrency(
        res.gross_by_currency && typeof res.gross_by_currency === "object"
          ? res.gross_by_currency
          // Fallback for a server that predates per-currency totals.
          : (res.gross ? { [BASE_CURRENCY]: res.gross } : {})
      );
      setDemo(false);
    } catch (e) {
      if (mine !== reqId.current) return;
      if (e && e.status === 0) {
        setDemo(true);
        setInvoices([]);
        setGrossByCurrency({});
        setTotal(0);
      } else {
        setError((e && e.message) || "Invoices could not be loaded.");
        setInvoices([]);
        setGrossByCurrency({});
        setTotal(0);
      }
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [statusFilter, fromDate, toDate, searchTerm, sort, dir, page]);

  useEffect(() => { load(); }, [load]);

  // Only needed to bind a manual invoice to a real tenant.
  useEffect(() => {
    listCompanies()
      .then((rows) => setCompanies(Array.isArray(rows) ? rows : []))
      .catch(() => setCompanies([]));
  }, []);

  // The live, admin-editable catalog — same source Change Plan uses — so the
  // Plan field lists what's actually active rather than a frozen copy.
  useEffect(() => {
    getPlans()
      .then((catalog) => setPlans(
        catalog && typeof catalog === "object"
          ? Object.entries(catalog).map(([id, p]) => ({ id, name: p.name || id }))
          : []
      ))
      .catch(() => setPlans([]));
  }, []);

  // Paging is SERVER-side now: `invoices` IS the current page, and the page
  // count comes from the database's own match count rather than from however
  // many rows happened to be sent.
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const paged = invoices;

  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);

  const dateRangeInvalid = !!fromDate && !!toDate && fromDate > toDate;

  // "₹1,20,000 + $490 collected" — each currency stated as itself. Base currency
  // first so the usual case reads normally; anything else follows, largest first.
  const collectedLabel = useMemo(() => {
    const entries = Object.entries(grossByCurrency).filter(([, v]) => Number(v) > 0);
    entries.sort(([a, av], [b, bv]) =>
      (a === BASE_CURRENCY ? -1 : b === BASE_CURRENCY ? 1 : Number(bv) - Number(av)));
    const count = `${total} invoice${total === 1 ? "" : "s"}`;
    if (entries.length === 0) return `${count} · nothing collected yet`;
    return `${count} · ${entries.map(([c, v]) => money(v, c)).join(" + ")} collected`;
  }, [grossByCurrency, total]);

  function closeCreate() {
    setShowCreate(false);
    setCreateError("");
    setNewInvoice({
      company_id: "", plan_id: "manual", amount: "", status: "unpaid",
      period_start: "", period_end: "", payment_reference: "", notes: "",
    });
  }

  async function createManualInvoice() {
    if (creating) return;
    const amount = Number(newInvoice.amount);
    if (!newInvoice.company_id) return setCreateError("Choose the company to bill.");
    if (!newInvoice.amount || !Number.isFinite(amount) || amount <= 0) return setCreateError("Enter an amount greater than zero.");
    if (newInvoice.period_start && newInvoice.period_end && newInvoice.period_start > newInvoice.period_end) {
      return setCreateError("The plan duration's start date is after its end date.");
    }
    setCreateError("");
    setCreating(true);
    try {
      await createInvoice({
        company_id: newInvoice.company_id,
        plan_id: newInvoice.plan_id || "manual",
        amount,
        // Explicit, never implied by a model default. Every charge the platform
        // makes itself is in the base currency; a manual invoice raised in
        // another one would need the gateway to have settled it, which by
        // definition it did not.
        currency: BASE_CURRENCY,
        status: newInvoice.status,
        // Bare "YYYY-MM-DD" read as UTC midnight / end-of-day — the same
        // whole-day convention this page's own date-range filter already uses.
        period_start: newInvoice.period_start ? `${newInvoice.period_start}T00:00:00Z` : undefined,
        period_end: newInvoice.period_end ? `${newInvoice.period_end}T23:59:59Z` : undefined,
        // Only meaningful once the invoice is actually marked paid.
        payment_reference: newInvoice.status === "paid" ? (newInvoice.payment_reference.trim() || undefined) : undefined,
        notes: newInvoice.notes.trim() || undefined,
      });
      closeCreate();
      toastSuccess("Invoice created.");
      await load();
    } catch (e) {
      // Keep the dialog open so the entered values survive a failure.
      setCreateError((e && e.message) || "Could not create the invoice.");
      toastError((e && e.message) || "Could not create the invoice.");
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Billing & Invoices"
        subtitle={collectedLabel}
        actions={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={16} /> Manual Invoice
          </Button>
        }
      />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          {/* There is no invoice fixture on this page — the old copy said
              "Showing demo data" over an empty table. */}
          <WifiOff size={15} /> Backend not reachable — invoices couldn&apos;t be loaded.
        </div>
      )}

      {/* filters */}
      <div className="card mb-4 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* One dropdown, not a row of chips — with only 3 real (reachable)
              statuses there's no need for a whole tab strip (#66). */}
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            aria-label="Filter by status"
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>{s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoice id, company or email…"
              aria-label="Search invoices"
              maxLength={100}
              className="h-8 w-64 rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            />
          </div>
          <span className="ml-2 text-xs text-slate-400">Date:</span>
          <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-brand-400 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-slate-200" />
          <span className="text-xs text-slate-400">to</span>
          {/* Disabled until a start date exists — "to" alone can't express a
              range, and it was pickable before "from" had any value (#68). */}
          <input type="date" value={toDate} min={fromDate || undefined} disabled={!fromDate}
            title={!fromDate ? "Pick a start date first" : undefined}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs text-slate-700 focus:border-brand-400 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200" />
          {(fromDate || toDate) && (
            <button type="button" onClick={() => { setFromDate(""); setToDate(""); setPage(1); }}
              className="text-xs text-slate-500 underline hover:text-slate-700 dark:text-slate-400">Clear</button>
          )}
        </div>
        {dateRangeInvalid && (
          <p className="text-xs text-rose-600 dark:text-rose-300">The start date is after the end date — no invoice can match.</p>
        )}
      </div>

      <LoadError message={error} onRetry={load} />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-collapse text-sm">
              <thead className="bg-slate-50/95 dark:bg-ink-900/95">
                <tr>
                  {/* Sorting runs in SQL, because paging does: sorting the page
                      the server chose would reorder 25 rows out of thousands and
                      present that as sorted. */}
                  {COLUMNS.map((c) => (
                    <th key={c.label} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {c.sort ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(c.sort)}
                          className="inline-flex items-center gap-1 hover:text-slate-800 dark:hover:text-white"
                          aria-label={`Sort by ${c.label.toLowerCase()}`}
                        >
                          {c.label}
                          {sort === c.sort
                            ? (dir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
                            : <ChevronsUpDown size={13} className="opacity-50" />}
                        </button>
                      ) : c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {paged.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-slate-400">No invoices match these filters.</td></tr>
                ) : paged.map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-white/5">
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-300">{invoiceNumber(inv.id)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 dark:text-white">{inv.company_name || inv.billing_email || "—"}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800 dark:text-white">{money(inv.amount, inv.currency)}</td>
                    <td className="px-4 py-3 text-xs capitalize text-slate-500 dark:text-slate-400">{String(inv.plan_id || "—").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold", STATUS_STYLE[inv.status] || STATUS_STYLE_DEFAULT)}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" />
                        {String(inv.status || "").replace(/_/g, " ").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400">
                      {fmtDate(inv.period_start)} — {fmtDate(inv.period_end)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(inv.created_at)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelected(inv)} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/5" title="View">
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-white/10">
              <span className="text-xs text-slate-400">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
              </span>
              {pages > 1 && (
                <div className="flex items-center gap-3">
                  <button disabled={safePage <= 1 || loading} onClick={() => setPage(safePage - 1)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">‹ Prev</button>
                  <span className="text-sm text-slate-500">Page {safePage} of {pages}</span>
                  <button disabled={safePage >= pages || loading} onClick={() => setPage(safePage + 1)} className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-40 dark:text-slate-300 dark:hover:bg-white/5">Next ›</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* View detail */}
      {/* Same helper as the table's own INVOICE_ID column — the title and the
          "Invoice ID" row used to show the full raw id while the table showed
          a plain slice of it, so the two never visually matched (#75). */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `Invoice ${invoiceNumber(selected.id)}` : ""} size="md"
        footer={<div className="flex justify-end"><Button variant="outline" size="sm" onClick={() => setSelected(null)}>Close</Button></div>}>
        {selected && (
          <div className="space-y-3 text-sm">
            {[
              ["Invoice ID", <span key="id" className="font-mono text-xs">{invoiceNumber(selected.id)}</span>],
              ["Company", selected.company_name || "—"],
              ["Billing email", selected.billing_email || "—"],
              ["Amount", money(selected.amount, selected.currency)],
              ["Plan", String(selected.plan_id || "—").replace(/_/g, " ")],
              ["Status", <span key="status" className={cn("rounded-full px-2.5 py-0.5 text-xs font-semibold", STATUS_STYLE[selected.status] || STATUS_STYLE_DEFAULT)}>{String(selected.status || "").toUpperCase()}</span>],
              ["Period", `${fmtDate(selected.period_start)} — ${fmtDate(selected.period_end)}`],
              ["Created", fmtDate(selected.created_at)],
              ["Payment ref", selected.razorpay_payment_id || "—"],
              // Only ever set on a manually-raised invoice — never shown at
              // all before, because the backend never persisted it anywhere
              // this modal could read it from (#71).
              ["Notes", selected.notes || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-2 dark:border-white/5">
                <span className="text-slate-500">{label}</span>
                <span className="font-medium text-slate-800 dark:text-white">{value}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Create manual invoice */}
      <Modal open={showCreate} onClose={closeCreate} title="Manual Invoice" size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={closeCreate} disabled={creating}>Cancel</Button>
            <Button size="sm" onClick={createManualInvoice} disabled={creating}>
              {creating ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Create invoice</>}
            </Button>
          </div>
        }>
        <div className="space-y-4">
          {createError && (
            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-200">{createError}</p>
          )}
          {/* Bound to a real tenant — a free-text company name could not be
              attached to an account, which is why this form saved nothing. */}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Company</span>
            <select value={newInvoice.company_id} onChange={(e) => setNewInvoice((f) => ({ ...f, company_id: e.target.value }))}
              className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white">
              <option value="">Select a company…</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {companies.length === 0 && <span className="mt-1 block text-xs text-slate-400">No companies loaded.</span>}
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Plan</span>
            <select value={newInvoice.plan_id} onChange={(e) => setNewInvoice((f) => ({ ...f, plan_id: e.target.value }))}
              className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white">
              <option value="manual">Manual / one-off charge</option>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Amount (₹)</span>
            <span className="relative block">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">₹</span>
              <input type="number" step="0.01" min="0.01" aria-label={`Invoice amount in ${BASE_CURRENCY}`} placeholder="0.00" value={newInvoice.amount}
                onChange={(e) => setNewInvoice((f) => ({ ...f, amount: e.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white pl-8 pr-4 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
            </span>
          </label>
          {/* Optional, like the backend accepts — a one-off correction may not
              cover any real service period at all. */}
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Plan duration (optional)</span>
            <div className="flex items-center gap-2">
              <input type="date" aria-label="Plan duration start" value={newInvoice.period_start}
                max={newInvoice.period_end || undefined}
                onChange={(e) => setNewInvoice((f) => ({ ...f, period_start: e.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
              <span className="shrink-0 text-xs text-slate-400">to</span>
              <input type="date" aria-label="Plan duration end" value={newInvoice.period_end}
                min={newInvoice.period_start || undefined}
                onChange={(e) => setNewInvoice((f) => ({ ...f, period_end: e.target.value }))}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white" />
            </div>
          </div>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Status</span>
            <select value={newInvoice.status} onChange={(e) => setNewInvoice((f) => ({ ...f, status: e.target.value }))}
              className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white">
              <option value="unpaid">Unpaid — awaiting payment</option>
              <option value="paid">Paid — already settled offline</option>
            </select>
          </label>
          {/* Only asked for once there's actually a payment to reference — an
              unpaid invoice has nothing to point this at yet. */}
          {newInvoice.status === "paid" && (
            <Input
              label="Payment reference (optional)"
              placeholder="Payment link, transaction id, or receipt no."
              maxLength={255}
              value={newInvoice.payment_reference}
              onChange={(e) => setNewInvoice((f) => ({ ...f, payment_reference: e.target.value }))}
            />
          )}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">Notes (optional)</span>
            <textarea
              rows={3}
              placeholder="Description…"
              maxLength={500}
              value={newInvoice.notes}
              onChange={(e) => setNewInvoice((f) => ({ ...f, notes: e.target.value }))}
              className="w-full resize-y rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/15 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
            />
          </label>
        </div>
      </Modal>
    </>
  );
}
